import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { MessageCircle } from 'lucide-react';
import { ObjectionType } from './types';

interface ObjectionsProps {
  objections: ObjectionType[];
  faithGroup?: string;
}

export const Objections: React.FC<ObjectionsProps> = ({ objections, faithGroup }) => {
  // Validate the objections array
  if (!objections || !Array.isArray(objections) || objections.length === 0) {
    console.log("No objections to display:", objections);
    return null;
  }
  
  // Filter out any invalid objections
  const validObjections = objections.filter(
    obj => obj && typeof obj === 'object' && obj.objection && obj.rebuttal
  );
  
  if (validObjections.length === 0) {
    console.log("No valid objections found:", objections);
    return null;
  }
  
  // Deduplicate objections - this is key to fixing the repetition issue
  const uniqueObjections: ObjectionType[] = [];
  const seen = new Set<string>();
  
  validObjections.forEach(obj => {
    // Create a normalized version of the objection to check for duplicates
    const normalizedObjection = obj.objection.toLowerCase().replace(/\s+/g, ' ').trim();
    
    // Only add this objection if we haven't seen it before
    if (!seen.has(normalizedObjection)) {
      seen.add(normalizedObjection);
      
      // Also clean up the objection to remove any repetition in the text itself
      const cleanedObjection = {
        objection: cleanRepetitiveText(obj.objection),
        rebuttal: cleanRepetitiveText(obj.rebuttal)
      };
      
      uniqueObjections.push(cleanedObjection);
    }
  });
  
  console.log(`Displaying ${uniqueObjections.length} objections for ${faithGroup || 'unknown'} faith group`);
  
  // Format the title based on context
  const getTitle = () => {
    if (faithGroup) {
      return `Common ${faithGroup.charAt(0).toUpperCase() + faithGroup.slice(1)} Objections & Rebuttals`;
    }
    return "Common Objections & Rebuttals";
  };
  
  return (
    <Card className="border-heaven-gold/10">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-heaven-gold text-lg">
          <MessageCircle className="h-5 w-5" />
          <span>{getTitle()}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {uniqueObjections.map((objection, i) => (
            <div key={i} className="mb-4 text-left">
              <p className="font-semibold text-gray-800">Objection {i + 1}: {objection.objection}</p>
              <p className="text-gray-700 pl-4 border-l-2 border-heaven-gold/30 mt-2">
                <span className="font-medium text-heaven-gold">Response:</span> {objection.rebuttal}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Cleans repetitive text by identifying and removing duplicate phrases/sentences
 */
function cleanRepetitiveText(text: string): string {
  if (!text) return '';
  
  // Remove label prefixes if they appear in the middle of text
  const cleanedText = text
    .replace(/\s*Objection \d+:\s*/g, ' ')
    .replace(/\s*Response:\s*/g, ' ')
    .trim();
    
  // Split into sentences
  const sentences = cleanedText.split(/(?<=[.!?])\s+/);
  const uniqueSentences: string[] = [];
  const seenSentences = new Set<string>();
  
  // Keep track of seen sentences to avoid duplicates
  for (const sentence of sentences) {
    // Normalize to check for near-duplicates
    const normalized = sentence.toLowerCase().replace(/[^\w\s]/g, '').trim();
    
    // Only add sentences we haven't seen before
    if (normalized.length > 0 && !seenSentences.has(normalized)) {
      seenSentences.add(normalized);
      uniqueSentences.push(sentence);
    }
  }
  
  return uniqueSentences.join(' ');
}
